/**
 * Unified Icon Service
 * Consolidates all icon implementations into a single, clean interface
 * Supports: Lucide icons, Iconify icons, and custom icons
 */

import React from 'react';
import { OfflineIcon } from '@/components/icons/IconifyOfflineProvider';
import { Globe } from 'lucide-react';

// Import icon data for offline use
import simpleIconsData from '@iconify-json/simple-icons/icons.json';
import tablerData from '@iconify-json/tabler/icons.json';
import ionData from '@iconify-json/ion/icons.json';
import biData from '@iconify-json/bi/icons.json';
import mdiData from '@iconify-json/mdi/icons.json';
import riData from '@iconify-json/ri/icons.json';

// Import existing services for backward compatibility
import { iconMappingService as lucideService } from './icon-mapping-lucide';
import { PLATFORM_ICONS } from './icon-library-enhanced';

// Types
export type IconStyle = 
  | 'outline' 
  | 'filled' 
  | 'outline-brand' 
  | 'filled-brand' 
  | 'minimal'
  | 'lucide'
  | 'brand';

export interface IconOptions {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
  iconStyle?: IconStyle;
}

export interface PlatformData {
  id: string;
  name: string;
  iconifyIds: {
    filled: string;
    outline?: string;
    alternates?: string[];
  };
  brandColor: string;
  brandColorSecondary?: string;
  category: string;
  aliases: string[];
}

export interface IconResult {
  element: React.ReactElement;
  source: 'lucide' | 'iconify' | 'custom' | 'fallback';
  platformData?: PlatformData;
  confidence: number;
}

class UnifiedIconService {
  private iconCache = new Map<string, string>();
  private platformCache = new Map<string, PlatformData | null>();

  /**
   * Main method to get an icon
   * Tries multiple sources in order of preference
   */
  getIcon(
    identifier: string | undefined,
    options: IconOptions = {}
  ): IconResult {
    const { 
      size = 20, 
      className = '', 
      style = {}, 
      color,
      iconStyle = 'filled-brand' 
    } = options;

    if (!identifier) {
      return this.getFallbackIcon(options);
    }

    // Try to get platform data first
    const platformData = this.getPlatformData(identifier);
    
    if (platformData && platformData.id !== 'generic') {
      // Use Iconify icons for known platforms
      const iconName = this.getIconForStyle(platformData, iconStyle);
      
      // Always try to render - Iconify will handle missing icons gracefully
      return {
        element: this.renderIconifyIcon(iconName, {
          size,
          className,
          style: { ...style, color: this.getColorForStyle(iconStyle, color, platformData.brandColor) },
        }),
        source: 'iconify',
        platformData,
        confidence: 1.0,
      };
    }

    // Try dynamic discovery in simple-icons before Lucide
    const dynamicIcon = this.tryFindSimpleIcon(identifier);
    if (dynamicIcon) {
      return {
        element: this.renderIconifyIcon(`simple-icons:${dynamicIcon}`, {
          size,
          className,
          style: { ...style, color },
        }),
        source: 'iconify',
        confidence: 0.8,
      };
    }

    // Try Lucide icons as fallback
    const lucideResult = lucideService.getIcon(identifier);
    if (lucideResult && lucideResult.icon) {
      return {
        element: React.cloneElement(
          lucideResult.icon as React.ReactElement<{
            width?: number | string;
            height?: number | string;
            className?: string;
            style?: React.CSSProperties;
          }>,
          {
            width: size,
            height: size,
            className,
            style: { ...style, color: color || lucideResult.color },
          },
        ),
        source: 'lucide',
        confidence: lucideResult.confidence,
      };
    }

    // Final fallback
    return this.getFallbackIcon(options);
  }

  /**
   * Get platform data by URL or platform name
   */
  getPlatformData(identifier: string): PlatformData | null {
    // Check cache first
    if (this.platformCache.has(identifier)) {
      return this.platformCache.get(identifier) || null;
    }

    // Try exact match first
    let platform = PLATFORM_ICONS[identifier.toLowerCase()];
    
    if (!platform) {
      // Try to extract platform from URL
      const domain = this.extractDomain(identifier);
      if (domain) {
        // Match the extracted domain against each platform's aliases. We used
        // to use bidirectional substring (`domain.includes(alias) ||
        // alias.includes(domain)`), which produced false positives — e.g. the
        // fallback token "link" matched LinkedIn because "link".includes("in")
        // is true (LinkedIn has "in" as an alias). Now we only match when the
        // domain equals the alias itself, or the alias's root label (so
        // `domain=facebook` still matches alias `facebook.com`).
        const domainLower = domain.toLowerCase();
        for (const [, data] of Object.entries(PLATFORM_ICONS)) {
          if (data.aliases.some(alias => {
            const a = alias.toLowerCase();
            const aliasRoot = a.includes('.') ? a.split('.')[0] : a;
            return domainLower === a || domainLower === aliasRoot;
          })) {
            platform = data as PlatformData;
            break;
          }
        }
      }
    }

    // Cache the result
    this.platformCache.set(identifier, platform || null);
    return platform || null;
  }

  /**
   * Get the best icon for a given style
   */
  private getIconForStyle(platform: PlatformData, style: IconStyle): string {
    const cacheKey = `${platform.id}-${style}`;
    
    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey)!;
    }

    let selectedIcon: string;

    switch (style) {
      case 'outline':
      case 'outline-brand':
      case 'minimal':
        if (platform.iconifyIds.outline) {
          selectedIcon = platform.iconifyIds.outline;
        } else {
          // Try to find outline version in alternates
          const outlineAlternate = platform.iconifyIds.alternates?.find(alt => 
            alt.includes('line') || 
            alt.includes('outline') || 
            alt.startsWith('tabler:') || 
            alt.startsWith('ri:')
          );
          selectedIcon = outlineAlternate || platform.iconifyIds.filled;
        }
        break;

      case 'filled':
      case 'filled-brand':
      case 'brand':
      default:
        selectedIcon = platform.iconifyIds.filled;
        // Filled is required, so we always have it
        break;
    }

    this.iconCache.set(cacheKey, selectedIcon);
    return selectedIcon;
  }

  /**
   * Check if an icon exists in the loaded data
   */
  private iconExists(iconName: string): boolean {
    const [prefix, name] = iconName.split(':');
    
    switch (prefix) {
      case 'simple-icons':
        return name in simpleIconsData.icons;
      case 'tabler':
        return name in tablerData.icons;
      case 'ion':
        return name in ionData.icons;
      case 'bi':
        return name in biData.icons;
      case 'mdi':
        return name in mdiData.icons;
      case 'ri':
        return name in riData.icons;
      default:
        // For unknown prefixes, return true and let Iconify handle it
        return true;
    }
  }

  /**
   * Render an Iconify icon using offline data
   */
  private renderIconifyIcon(
    iconName: string,
    options: { size?: number; className?: string; style?: React.CSSProperties }
  ): React.ReactElement {
    return React.createElement(OfflineIcon, {
      icon: iconName,
      width: options.size,
      height: options.size,
      className: options.className,
      style: options.style,
    });
  }

  /**
   * Render a direct SVG icon (for simple-icons)
   */
  renderDirectSvg(
    iconName: string,
    options: IconOptions = {}
  ): React.ReactElement | null {
    const { size = 20, className = '', style = {}, color } = options;
    
    // Remove prefix if present
    const name = iconName.replace('simple-icons:', '');
    const iconData = (simpleIconsData.icons as any)[name];
    
    if (!iconData) {
      return null;
    }

    return React.createElement('svg', {
      width: size,
      height: size,
      viewBox: `0 0 ${iconData.width || 24} ${iconData.height || 24}`,
      className,
      style: { fill: color || 'currentColor', ...style },
      dangerouslySetInnerHTML: { __html: iconData.body },
      xmlns: 'http://www.w3.org/2000/svg',
    });
  }

  /**
   * Get color based on style
   */
  private getColorForStyle(
    style: IconStyle,
    customColor?: string,
    brandColor?: string
  ): string | undefined {
    if (customColor) return customColor;
    
    switch (style) {
      case 'outline-brand':
      case 'filled-brand':
      case 'brand':
        return brandColor;
      default:
        return undefined;
    }
  }

  /**
   * Get fallback icon
   */
  private getFallbackIcon(options: IconOptions): IconResult {
    const { size = 20, className = '', style = {}, color } = options;
    
    return {
      element: React.createElement(Globe, {
        width: size,
        height: size,
        className,
        style: { ...style, color: color || '#6B7280' },
      }),
      source: 'fallback',
      confidence: 0,
    };
  }

  /**
   * Extract domain from URL
   */
  private extractDomain(url: string): string | null {
    try {
      // Handle full URLs
      if (url.includes('://')) {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '').split('.')[0];
      }
      
      // Handle partial URLs
      const domain = url.replace('www.', '').split('.')[0].split('/')[0];
      return domain;
    } catch {
      return null;
    }
  }

  /**
   * Get library statistics
   */
  getStats(): Record<string, number> {
    return {
      'simple-icons': Object.keys(simpleIconsData.icons).length,
      'tabler': Object.keys(tablerData.icons).length,
      'ion': Object.keys(ionData.icons).length,
      'bi': Object.keys(biData.icons).length,
      'mdi': Object.keys(mdiData.icons).length,
      'ri': Object.keys(riData.icons).length,
      'platforms': Object.keys(PLATFORM_ICONS).length,
    };
  }

  /**
   * Preload icons for better performance
   */
  preloadIcons(identifiers: string[]): void {
    identifiers.forEach(id => {
      const platformData = this.getPlatformData(id);
      if (platformData) {
        // Cache the icon selections
        ['outline', 'filled', 'filled-brand'].forEach(style => {
          this.getIconForStyle(platformData, style as IconStyle);
        });
      }
    });
  }

  /**
   * Try to find icon in simple-icons collection
   */
  private tryFindSimpleIcon(identifier: string): string | null {
    // Extract potential icon name from identifier
    const cleanName = this.extractDomain(identifier) || identifier;
    const variations = [
      cleanName.toLowerCase(),
      cleanName.toLowerCase().replace(/[^a-z0-9]/g, ''),
      cleanName.toLowerCase().replace(/\s+/g, ''),
      cleanName.toLowerCase().replace(/-/g, ''),
      cleanName.toLowerCase().replace(/_/g, ''),
    ];

    // Check if any variation exists in simple-icons
    for (const variant of variations) {
      if (variant in simpleIconsData.icons) {
        return variant;
      }
    }

    // Try common variations for known services
    const commonMappings: Record<string, string> = {
      'ycombinator': 'ycombinator',
      'hackernews': 'ycombinator',
      'hn': 'ycombinator',
      'devto': 'devdotto',
      'dev': 'devdotto',
    };

    const lowerIdentifier = cleanName.toLowerCase();
    if (commonMappings[lowerIdentifier]) {
      const mappedName = commonMappings[lowerIdentifier];
      if (mappedName in simpleIconsData.icons) {
        return mappedName;
      }
    }

    return null;
  }

  /**
   * Clear caches
   */
  clearCache(): void {
    this.iconCache.clear();
    this.platformCache.clear();
  }
}

// Export singleton instance
export const iconService = new UnifiedIconService();

// Export for backward compatibility
export { PLATFORM_ICONS } from './icon-library-enhanced';
export type { PlatformIconData } from './icon-renderer-enhanced';